import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckView } from '../features/checkView/hooks/useCheckView';
import { CheckViewForm } from '../features/checkView/components/CheckViewForm';
import { CheckViewFilters } from '../features/checkView/components/CheckViewFilters';
import { CheckViewTable } from '../features/checkView/components/CheckViewTable';
import { EditVideoDialog } from '../features/checkView/components/EditVideoDialog';
import { ViewYoutube } from '../services/viewYoutubeService';
import { CheckCircle } from 'lucide-react';

const CheckViewPage: React.FC = () => {
    const { t } = useTranslation('common');
    const {
        youtubeUrl, setYoutubeUrl, checkViewLoading, handleCheckView,
        videos, loading,
        dateStart, setDateStart, dateEnd, setDateEnd,
        statusFilter, setStatusFilter, channelFilter, setChannelFilter,
        search, setSearch, channelOptions,
        page, setPage, total, totalPages,
        checkViewEnabled, loadingSetting,
        deleteVideo, updateVideo
    } = useCheckView();

    const [editingVideo, setEditingVideo] = useState<ViewYoutube | null>(null);
    const [showEdit, setShowEdit] = useState(false);

    const onEdit = (video: ViewYoutube) => {
        setEditingVideo(video);
        setShowEdit(true);
    };

    if (loadingSetting) return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
        </div>
    );

    if (!checkViewEnabled) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-4">
                <div className="bg-red-50 p-6 rounded-full mb-4">
                     <span className="text-4xl">🚫</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('checkView.messages.disabled')}</h2>
                <p className="text-gray-500">Please contact administrator.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                 <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg text-white">
                        <CheckCircle size={32} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                            {t('checkView.header.title')}
                        </h1>
                        <p className="text-gray-500 mt-1 text-lg">
                             {t('checkView.header.subtitle')}
                        </p>
                    </div>
                </div>

                <CheckViewForm 
                    youtubeUrl={youtubeUrl}
                    setYoutubeUrl={setYoutubeUrl}
                    handleCheckView={handleCheckView}
                    isLoading={checkViewLoading}
                />

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                     <div className="mb-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                             {t('checkView.management.title')}
                             <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-500 font-normal">
                                {total} videos
                             </span>
                        </h2>
                        <CheckViewFilters 
                            dateStart={dateStart} setDateStart={setDateStart}
                            dateEnd={dateEnd} setDateEnd={setDateEnd}
                            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                            channelFilter={channelFilter} setChannelFilter={setChannelFilter}
                            search={search} setSearch={setSearch}
                            channelOptions={channelOptions}
                        />
                     </div>

                    <CheckViewTable 
                        videos={videos}
                        loading={loading}
                        page={page}
                        setPage={setPage}
                        perPage={20}
                        total={total}
                        totalPages={totalPages}
                        onDelete={deleteVideo}
                        onEdit={onEdit}
                    />
                </div>
            </div>

            <EditVideoDialog 
                isOpen={showEdit}
                onClose={() => setShowEdit(false)}
                video={editingVideo}
                onUpdate={updateVideo}
            />
        </div>
    );
};

export default CheckViewPage;
